import React, { useEffect } from 'react'
import { Box, Flex } from '@fundamentals'
import { localizedStrings } from '@core/strings'
import {
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  LoadingButton,
  TextInput,
} from '@common/material'
import { Check, Clear } from '@mui/icons-material'
import { useModal } from '@common/GlobalModal'
import { ModalDialogContainer } from '@common/modal'
import { useFunction, useHtml, useNavigationContext } from '@common/hooks'
import { Palette } from '@core/theme/color-palette-siteassist'
import {
  BaseTextFieldProps,
  Collapse,
  FilledInputProps,
  SlotProps,
  ToggleButton,
} from '@mui/material'
import styled from '@emotion/styled'
import { Controller, useForm } from 'react-hook-form'
import { CreateTaskPage } from '@modules/tasks/createTask/CreateTaskPage'
import { ITemplateQuestion } from '@core/entities/template/TemplateQuestion'
import { AttachmentInput } from '@elements/Questions/AttachmentInput'
import { AttachmentTypes } from '@core/components/templateAnswersForm'
import useConstants from '@core/providers/useConstants'
import {
  IInspectionItemAnswer,
  InspectionItemAnswer,
} from '@core/entities/InspectionItemAnswer'
import { ITaskTemplate } from '@core/entities/task/TaskTemplate'
import { ITemplateProjectLocations } from '@core/entities/template/TemplateProjectLocations'
import ConfirmModal from '@common/modal/ConfirmModal'

export enum ResponseValue {
  YES = 'YES',
  NO = 'NO',
  NA = 'NA',
}

export const responseOptionsObject = {
  [ResponseValue.YES]: {
    label: <Check fontSize='small' />,
    value: ResponseValue.YES,
  },
  [ResponseValue.NO]: {
    label: <Clear fontSize='small' />,
    value: ResponseValue.NO,
  },
  [ResponseValue.NA]: {
    label: 'N/A',
    value: ResponseValue.NA,
  },
}

const ResponseButton = styled(ToggleButton)<{
  borderColor: string
  bgcolor: string
  iconColor: string
}>`
  width: 100px;
  height: 32px;
  border-radius: 24px;

  &.MuiToggleButton-root {
    border: 1px solid
      ${({ borderColor, disabled, selected }) =>
        disabled ? Palette.Grey[selected ? 50 : 400] : borderColor};
    color: ${({ iconColor, disabled, selected }) =>
      disabled ? Palette.Grey[selected ? 50 : 400] : iconColor};
    background-color: ${({ bgcolor, disabled, selected }) =>
      disabled ? Palette.Grey[selected ? 400 : 50] : bgcolor};

    &.Mui-selected:hover {
      background-color: ${({ bgcolor, disabled, selected }) =>
        disabled ? Palette.Grey[selected ? 400 : 50] : bgcolor};
    }
  }

  &:focus {
    outline: none; // Removes the focus border
  }
`

type AddResponseModalProps = {
  question: ITemplateQuestion
  onSave: (response: any) => void
  onDelete: (id: string) => void
  projectId: number
  first?: boolean
  initialValues: any
  attachmentTypes: AttachmentTypes[]
  inspectionTaskTemplates?: Array<ITaskTemplate>
  projectLocations?: ITemplateProjectLocations
  openTaskModal?: boolean
}

type CustomInputProps = SlotProps<
  React.ElementType<FilledInputProps>,
  {},
  BaseTextFieldProps
> & {
  'data-test'?: string
}

export const InspectionResponseForm: React.FC<AddResponseModalProps> = ({
  question,
  onSave,
  projectId,
  initialValues,
  attachmentTypes,
  inspectionTaskTemplates,
  projectLocations,
  onDelete,
  openTaskModal,
}) => {
  const { constants } = useConstants()
  const { organisationId } = useNavigationContext()
  const { hideModal, showModal, hideAllModals } = useModal()
  const { html } = useHtml(question.body)

  // filter out null values
  const inspectionTaskTemplateIds = question.inspectionTaskTemplateIds.filter(
    (id) => !!id,
  )

  const { setValue, handleSubmit, watch, control, getValues } = useForm<{
    answer: IInspectionItemAnswer
  }>({
    defaultValues: {
      answer: {
        id: initialValues?.id || null,
        inspectionItemAnswerType:
          initialValues.inspectionItemAnswerType || null,
        first: initialValues.first || false,
        taskPayload:
          initialValues.taskPayload || initialValues.taskPayloadResult || null,
        submissionText: initialValues.submissionText || null,
        submissionProofs: initialValues.submissionProofs || [],
      },
    },
  })

  const handleDeleteAnswer = useFunction(() => {
    showModal(
      <ConfirmModal
        onConfirm={() => {
          hideAllModals()
          onDelete(initialValues.id)
        }}
      >
        {localizedStrings.areYouSureDeleteResponse}
      </ConfirmModal>,
    )
  })

  const firstAnswer = watch('answer.first')
  const taskPayload = watch('answer.taskPayload')

  const taskTemplateData = (id: number) =>
    inspectionTaskTemplates?.find((template) => template.id === id)

  const handleOpenTaskModal = useFunction(() => {
    showModal(
      <ModalDialogContainer maxWidth='lg'>
        <CreateTaskPage
          actionButtons={
            initialValues?.id
              ? [
                  {
                    variant: 'outlined',
                    children: localizedStrings.delete,
                    onClick: handleDeleteAnswer,
                    color: 'error',
                  },
                ]
              : []
          }
          mainActionButtonLabel={!!taskPayload && localizedStrings.update}
          taskTemplates={
            inspectionTaskTemplateIds?.length > 0 &&
            inspectionTaskTemplates.filter((template) =>
              inspectionTaskTemplateIds.includes(template.id),
            )
          }
          disableProjectSelect
          disableTaskTemplateSelect={inspectionTaskTemplateIds?.length === 1}
          disableRouting
          enableDrafts={false}
          organisationId={organisationId}
          onBack={() => {
            if (initialValues?.id) {
              hideAllModals()
            } else {
              hideModal()
              setValue('answer.inspectionItemAnswerType', null)
            }
          }}
          activeProjectId={projectId}
          initialValues={{
            ...taskPayload,
            projectLocations: taskPayload?.projectLocations ?? projectLocations,
            attachments: taskPayload?.attachments || [],
            template: taskPayload
              ? taskTemplateData(taskPayload.taskTemplate.id)
              : inspectionTaskTemplateIds?.length === 1
              ? taskTemplateData(inspectionTaskTemplateIds[0])
              : null,
            questions: initialValues.taskPayload?.stepAnswers?.answers,
          }}
          onSubmit={(payload) => {
            hideModal()
            const attachments = payload.attachments.data
              ? payload.attachments
              : { data: payload.attachments }

            onSave({
              answer: {
                ...getValues('answer'),
                taskPayload: {
                  stepAnswers: { answers: payload.questions },
                  taskTemplateId: payload.template.currentVersion.templateId,
                  taskTemplateVersionId: payload.template.currentVersion.id,
                  assigneeUserId: payload.assigneeUser.id,
                  attachments,
                  summary: payload.summary,
                  priority: payload.priority,
                  dueDate: payload.dueDate,
                  projectLocations: payload.projectLocations,
                  // local variables
                  assigneeUser: payload.assigneeUser,
                  taskTemplate: {
                    id: payload.template.id,
                    name: payload.template.name,
                  },
                },
              },
            })
          }}
        />
      </ModalDialogContainer>,
      {
        modalId: 'complete-task-modal',
      },
    )
  })

  useEffect(() => {
    if (openTaskModal) {
      handleOpenTaskModal()
    }
  }, [openTaskModal])

  if (initialValues?.taskPayload) {
    // when editing taskPayload values we can skip the initial modal
    return null
  }

  return (
    <ModalDialogContainer>
      <form onSubmit={handleSubmit(onSave)}>
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20 }}
        >
          {initialValues?.id
            ? localizedStrings.changeResponse
            : localizedStrings.addResponse}
          <IconButton aria-label='close-modal' onClick={hideModal}>
            <Clear />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box
            dangerouslySetInnerHTML={{ __html: html }}
            color='grey.600'
            data-test='body'
            sx={{
              '& p': { mb: 1, fontSize: '14px' },
              '& ol': { paddingInlineStart: '40px' },
            }}
          />
          <Controller
            name='answer'
            control={control}
            render={({ field, fieldState }) => {
              return (
                <>
                  <Flex gap={1}>
                    {Object.values(responseOptionsObject)
                      .filter((option) =>
                        option.value === ResponseValue.NA ? firstAnswer : true,
                      )
                      .map(({ value, label }) => {
                        const answerTypeColors =
                          InspectionItemAnswer.getAnswerTypeConfigColors(value)
                        const selected =
                          field.value.inspectionItemAnswerType === value
                        const key = selected ? 'selected' : 'default'
                        return (
                          <ResponseButton
                            data-test={`inspectionItemAnswerType-${value}`}
                            key={value}
                            value={value}
                            bgcolor={answerTypeColors[key].backgroundColor}
                            borderColor={answerTypeColors[key].borderColor}
                            iconColor={answerTypeColors[key].iconColor}
                            disabled={
                              !!initialValues?.id &&
                              initialValues.inspectionItemAnswerType !==
                                ResponseValue.NA
                            }
                            selected={selected}
                            onClick={() => {
                              field.onChange({
                                ...field.value,
                                inspectionItemAnswerType: value,
                              })
                              value === ResponseValue.NO &&
                                handleOpenTaskModal()
                              value === ResponseValue.NA &&
                                onSave({
                                  answer: {
                                    ...field.value,
                                    inspectionItemAnswerType: value,
                                    first: true,
                                  },
                                })
                            }}
                          >
                            {label}
                          </ResponseButton>
                        )
                      })}
                  </Flex>
                  <Collapse
                    in={
                      field.value?.inspectionItemAnswerType ===
                      ResponseValue.YES
                    }
                  >
                    <>
                      <TextInput
                        value={field.value.submissionText}
                        onChange={(e) => {
                          field.onChange({
                            ...field.value,
                            submissionText: e.target.value,
                          })
                        }}
                        minRows={3}
                        placeholder={localizedStrings.questionAdditionalInformation(
                          question.requiresAdditionalInformation === 'OPTIONAL',
                        )}
                        multiline
                        fullWidth
                        error={
                          !!fieldState.error && !field.value.submissionText
                        }
                        sx={{ mt: 2 }}
                        slotProps={{
                          input: {
                            'data-test': 'additional-info-input',
                          } as CustomInputProps,
                        }}
                      />
                      <AttachmentInput
                        heading={localizedStrings.proofs}
                        enableLinkSiteBoxDocuments={true}
                        projectId={projectId}
                        mt={2}
                        size='small'
                        inputProps={{
                          value: [
                            ...(field.value?.submissionProofs?.map((proof) => ({
                              ...proof,
                              type: 'image',
                            })) || []),
                            ...(field.value?.submissionDocuments?.map(
                              (document) => ({
                                ...document,
                                type: 'application/pdf',
                              }),
                            ) || []),
                          ],
                          onChange: (proofs) => {
                            const submissionProofs = proofs.filter((proof) =>
                              proof.type.includes('image'),
                            )
                            const submissionDocuments = proofs.filter((proof) =>
                              proof.type.includes('pdf'),
                            )

                            field.onChange({
                              ...field.value,
                              submissionProofs,
                              submissionDocuments,
                            })
                            field.onBlur()
                          },
                        }}
                        attachmentTypes={attachmentTypes}
                        maxFileSize={
                          constants.max_question_attachment_size_in_bytes
                        }
                        dataTest={`inspection-additional-info-attachment-input`}
                      />
                      <Flex justifyContent='flex-end' gap={2} mt={2}>
                        {initialValues?.id &&
                        initialValues.inspectionItemAnswerType !==
                          ResponseValue.NA ? (
                          <Button
                            variant='outlined'
                            onClick={handleDeleteAnswer}
                            color='error'
                          >
                            {localizedStrings.delete}
                          </Button>
                        ) : (
                          <Button variant='outlined' onClick={hideModal}>
                            {localizedStrings.cancel}
                          </Button>
                        )}
                        <LoadingButton
                          variant='contained'
                          type='submit'
                          data-test='save-button'
                        >
                          {localizedStrings.save}
                        </LoadingButton>
                      </Flex>
                    </>
                  </Collapse>
                </>
              )
            }}
          />
        </DialogContent>
      </form>
    </ModalDialogContainer>
  )
}
